/**
 * Battle entry gate — v0M13.1 (2026-07-30).
 *
 * Locked at `BATTLES.md` §2.7.2.a: a cohort enters the battle only if
 * BOTH of its persistent stats exceed the entry-gate thresholds:
 *   - `stamina > ENTRY_GATE_STAMINA_MIN` (default 10)
 *   - `morale > ENTRY_GATE_MORALE_MIN` (default 10)
 *
 * Ineligible cohorts stay on `Army.cohortIds` and are never placed on the
 * battlescape — they do not appear in `Battle.fieldCohorts`, do not take
 * damage, are not targeted by AI, and are not consumed by any of the
 * §2.7.3-§2.7.6 phases. On battle conclusion, reserve cohorts survive
 * intact regardless of outcome (they were never at risk).
 *
 * Thresholds intentionally match the MFix1.5 / MFix15
 * `WEAK_ARMY_MAX_STAMINA` / `WEAK_ARMY_MAX_MORALE` constants in
 * `zoc-detection.ts`. That keeps the two systems consistent: an army
 * whose every cohort fails the entry gate is exactly what `isArmyWeak`
 * treats as "weak" — so the strategic-map ZoC-contact destruction path
 * already handles zero-eligible armies for army-vs-army engagements. The
 * siege + fort-assault paths call the zero-eligible helper here
 * explicitly (they don't route through `zocDetectionSystem`).
 */

import type { Cohort, GameState } from './state';

/**
 * Stamina threshold. A cohort with `stamina > this` passes the stamina
 * half of the entry gate.
 */
export const ENTRY_GATE_STAMINA_MIN = 10;

/**
 * Morale threshold. A cohort with `morale > this` passes the morale
 * half of the entry gate.
 */
export const ENTRY_GATE_MORALE_MIN = 10;

/**
 * True iff the cohort is eligible to enter a battle per §2.7.2.a. Both
 * stamina AND morale must exceed their thresholds. A cohort failing
 * either half stays with its Army as reserve.
 *
 * Also filters out zero-figure cohorts — a cohort with no living figures
 * has nothing to send to the field, regardless of stamina/morale (which
 * are figure-cluster stats and become meaningless at count 0).
 */
export function isCohortEligibleForBattle(cohort: Cohort): boolean {
  if (cohort.aliveFigureCount <= 0) return false;
  return cohort.stamina > ENTRY_GATE_STAMINA_MIN && cohort.morale > ENTRY_GATE_MORALE_MIN;
}

/**
 * True iff `army` has at least one cohort that passes the entry gate.
 * Callers on the siege + fort-assault paths use this to short-circuit
 * battle creation for zero-eligible attackers (mirrors the MFix1.5
 * weak-army-destruction pattern that `zoc-detection.ts` runs for
 * army-vs-army engagements).
 *
 * Returns false for empty-roster armies (they'd already be candidates
 * for `disbandArmy`).
 */
export function armyHasEligibleCohorts(
  army: { readonly cohortIds: readonly import('./state').CohortId[] },
  state: GameState,
): boolean {
  if (army.cohortIds.length === 0) return false;
  for (const cohortId of army.cohortIds) {
    const cohort = state.cohorts.get(cohortId);
    if (!cohort) continue;
    if (isCohortEligibleForBattle(cohort)) return true;
  }
  return false;
}
